export type ElectricalTariff =
  | "2.0TD"
  | "3.0TD"
  | "6.1TD"
  | "6.2TD"
  | "6.3TD"
  | "6.4TD"
  | "other"
  | "unknown";
export type ElectricalPeriod = "P1" | "P2" | "P3" | "P4" | "P5" | "P6";
export type ElectricalProvenance =
  | "document_text"
  | "ocr"
  | "human_review"
  | "manual";
export type ElectricalReading = {
  period: ElectricalPeriod;
  value: number;
  unit: "kW" | "kWh";
  startDate?: string;
  endDate?: string;
  billingIntervalSource?: "invoice_billing_period";
  tariffAtCapture?: ElectricalTariff;
  source?: {
    documentId?: number;
    page?: number;
    sourceText: string;
    provenance: ElectricalProvenance;
  };
};

export function parseSpanishDecimal(raw: string): number | null {
  const normalized = raw.trim().replace(/\s/g, "");
  if (
    !/^(?:\d{1,3}(?:\.\d{3})+|\d+)?(?:,\d+)?$/.test(normalized) ||
    !/\d/.test(normalized)
  )
    return null;
  const value = Number(normalized.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(value) && value >= 0 ? value : null;
}

function isoDate(raw: string): string | undefined {
  const match = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (!match) return undefined;
  const [, day, month, year] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (
    date.getUTCFullYear() !== Number(year) ||
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCDate() !== Number(day)
  )
    return undefined;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

/** Parse only explicit period-labelled values. Repeated pages are deduplicated, never added. */
export function normalizePeriodReadings(
  text: string,
  unit: "kW" | "kWh",
  provenance: ElectricalProvenance,
  page?: number,
  documentId?: number
): ElectricalReading[] {
  const results: ElectricalReading[] = [];
  for (const line of text
    .split(/\r?\n/)
    .map(value => value.trim())
    .filter(Boolean)) {
    const match = line.match(
      /\b(P[1-6])\b[^\d\n]{0,80}(\d[\d., ]*)\s*(?:kW|kWh)\b/i
    );
    if (!match) continue;
    const value = parseSpanishDecimal(match[2]);
    if (value == null) continue;
    const dates = Array.from(
      line.matchAll(/\b(\d{1,2}[\/-]\d{1,2}[\/-]\d{4})\b/g)
    )
      .map(item => isoDate(item[1]))
      .filter((x): x is string => Boolean(x));
    const reading: ElectricalReading = {
      period: match[1].toUpperCase() as ElectricalPeriod,
      value,
      unit,
      ...(dates[0] ? { startDate: dates[0] } : {}),
      ...(dates[1] ? { endDate: dates[1] } : {}),
      source: {
        ...(documentId ? { documentId } : {}),
        ...(page ? { page } : {}),
        sourceText: line.slice(0, 500),
        provenance,
      },
    };
    if (
      reading.startDate &&
      reading.endDate &&
      reading.startDate > reading.endDate
    )
      continue;
    const duplicate = results.some(
      existing =>
        existing.period === reading.period &&
        existing.value === reading.value &&
        existing.startDate === reading.startDate &&
        existing.endDate === reading.endDate &&
        existing.unit === reading.unit
    );
    if (!duplicate) results.push(reading);
  }
  return results;
}

export function supportedPeriods(
  tariff: ElectricalTariff,
  kind: "contractedPower" | "consumptionPeriods"
): ElectricalPeriod[] {
  if (tariff === "2.0TD")
    return (
      kind === "contractedPower" ? ["P1", "P2"] : ["P1", "P2", "P3"]
    ) as ElectricalPeriod[];
  return ["P1", "P2", "P3", "P4", "P5", "P6"];
}
